import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

/**
 * Typed, validated SPA environment (the app composition layer).
 *
 * This is the single place where raw `import.meta.env` is read and validated.
 * The build fails fast if a required VITE_* var is missing or malformed.
 *
 * Ports & adapters: integration packages (@repo/auth, @repo/payments,
 * @repo/analytics) must receive their config via their typed interfaces — wire
 * them from `env` here at composition time rather than letting them read raw env.
 */
export const env = createEnv({
  /**
   * Only `VITE_`-prefixed vars are exposed to the client bundle by Vite, so we
   * enforce that prefix and read from `import.meta.env`.
   */
  clientPrefix: "VITE_",
  client: {
    VITE_API_URL: z.string().url(),
    VITE_OIDC_ISSUER: z.string().url(),
    VITE_OIDC_CLIENT_ID: z.string().min(1),
    VITE_OIDC_REDIRECT_URI: z.string().url(),
    VITE_OIDC_SCOPE: z.string().min(1).default("openid profile email"),
    VITE_STRIPE_PUBLISHABLE_KEY: z.string().min(1),
    VITE_POSTHOG_PROJECT_TOKEN: z.string().min(1),
    VITE_POSTHOG_HOST: z.string().url().optional(),
    VITE_OTEL_EXPORTER_OTLP_ENDPOINT: z.string().url().optional(),
  },
  /**
   * Vite exposes client env on `import.meta.env`.
   */
  runtimeEnv: import.meta.env,
  /**
   * Treat empty strings as undefined so a blank var fails `min(1)` validation
   * instead of silently passing through.
   */
  emptyStringAsUndefined: true,
});
